import React, { useMemo } from 'react';
import { Row, Col, Alert, Icon, DisplayField, Card } from '@digitools/honest-react';
import { useTranslation } from 'react-i18next';
import checkDate from '../../../utilities/checkDate';
import useAuthentication from '../../../../../hooks/useAuthentication';
import titleCase from 'src/packages/status/utilities/titleCase';
import styled from 'styled-components';
import { PENDING_STATUS_CODES, DENIED_STATUS_CODES } from '../../../constants/StatusCodes';
import useStatusDataContext from 'src/packages/status/hooks/useStatusDataContext';
import getGlossaryTermById from 'src/packages/status/utilities/getGlossaryTermById';
import useTranslate from 'src/hooks/useTranslate';
import useTealium from 'src/hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { useTooltipTracking } from 'src/hooks/useTooltipTracking';
const StatusMessage = styled('div') `
  color: ${({ theme }) => theme?.palette?.darkGray};
  font-style: italic;
  font-size: 0.9rem;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
`;
const StyledFlexContainerDiv = styled('div') `
  display: flex;
  flex-wrap: wrap;
  justify-content: left;
  column-gap: 1.1%;
  row-gap: 10px;
  margin-bottom: 10px;
  @media (max-width: 440px) {
    column-gap: 4%;
  }
`;
const StyledFourthColumnDiv = styled('div') `
  @media (min-width: 440px) {
    margin: 0px 2px 0px 2px;
    flex: 0 0 23.7%;
  }
  @media (max-width: 440px) {
    flex: 0 0 48%;
    margin-bottom: 15px;
  }
`;
const Error = () => (<Alert type={'error'}>
    <span>
      <strong>Error!</strong>&nbsp;Unable to load claims dates.
    </span>
  </Alert>);
const ClaimDatesCard = ({ claim }) => {
    const { user } = useAuthentication();
    const { trackEvent } = useTealium();
    const { t } = useTranslation();
    const { t: newTranslate } = useTranslate();
    const { glossary } = useStatusDataContext();
    const { trackTooltipHover, trackTooltipClick, handleTooltipClose } = useTooltipTracking({
        eventType: TEALIUM_EVENT.EVENT_TYPE.CLAIM_DATES
    });
    const glossaryTerms = useMemo(() => {
        return {
            determinationDate: getGlossaryTermById(glossary, '24'),
            benefitBeginDate: getGlossaryTermById(glossary, '8'),
            maxBenefitDate: getGlossaryTermById(glossary, '132'),
            rtwEstimated: getGlossaryTermById(glossary, '133'),
            rtwModified: getGlossaryTermById(glossary, '134'),
            rtwActual: getGlossaryTermById(glossary, '135'),
        };
    }, [glossary]);
    const isSTDLTD = claim.prodType === 'STD' ||
        claim.prodType === 'Short Term Disability' ||
        claim.prodType === 'LTD' ||
        claim.prodType === 'Long Term Disability';
    //if it's not the status we want, we will render it, otherwise hide
    const isNotPendingInprogressStatus = !PENDING_STATUS_CODES.includes(claim.clmSttusCde);
    const isNotDeniedStatus = !DENIED_STATUS_CODES.includes(claim.clmSttusCde);
    if (!claim) {
        return <Error />;
    }
    return (<Card data-testid={'claimDates'} selectable={false} fontSize='large' small={true}>
      <StyledFlexContainerDiv>
        <StyledFourthColumnDiv>
          <DisplayField label={titleCase(t('claimReceivedText').toString())} value={claim.clmRecdDte && claim.clmRecdDte.split(' ')[0]}/>
        </StyledFourthColumnDiv>
        <StyledFourthColumnDiv>
          <DisplayField label={titleCase(t('lastDateWorkedText').toString())} value={checkDate(claim.clmLastWrkDte)}/>
        </StyledFourthColumnDiv>
        <StyledFourthColumnDiv>
          <DisplayField data-private={true} label={titleCase(t('dateOfDisabilityText').toString())} value={checkDate(claim.clmDisabDte)}/>
        </StyledFourthColumnDiv>
        {isNotPendingInprogressStatus && (<StyledFourthColumnDiv>
            <DisplayField onTooltipOpenHover={trackTooltipHover('determiniation date')} onTooltipOpenClick={trackTooltipClick('determiniation date')} onTooltipClose={handleTooltipClose} data-private={true} label={titleCase(t('determinationDateText').toString())} value={checkDate(claim.clmDtrmDte)} tooltip={newTranslate(glossaryTerms.determinationDate)}/>
          </StyledFourthColumnDiv>)}

        {isNotDeniedStatus && (<StyledFourthColumnDiv>
            <DisplayField onTooltipOpenHover={trackTooltipHover('benefits begin date')} onTooltipOpenClick={trackTooltipClick('benefits begin date')} onTooltipClose={handleTooltipClose} label={titleCase(t('benefitsBeginText').toString())} value={checkDate(claim.benBeginDte)} tooltip={newTranslate(glossaryTerms.benefitBeginDate)}/>
          </StyledFourthColumnDiv>)}
        {isNotPendingInprogressStatus && isNotDeniedStatus && (<StyledFourthColumnDiv>
            <DisplayField onTooltipOpenHover={trackTooltipHover('potential max benefit end date')} onTooltipOpenClick={trackTooltipClick('potential max benefit end date')} onTooltipClose={handleTooltipClose} label={titleCase(t('benefitsEndText').toString())} value={checkDate(claim.benEndDte)} tooltip={newTranslate(glossaryTerms.maxBenefitDate)}/>
          </StyledFourthColumnDiv>)}
        {isNotPendingInprogressStatus && isNotDeniedStatus && (<StyledFourthColumnDiv>
            <DisplayField label={user?.customerInContext?.smallMid
                ? `${isSTDLTD ? '*' : ''} ${titleCase(t('approvedToText').toString())}`
                : `${isSTDLTD ? '*' : ''} ${titleCase(t('approvedThroughText').toString())}`} value={checkDate(claim.disabApprvThruDte)}/>
          </StyledFourthColumnDiv>)}
        {isNotDeniedStatus && (<StyledFourthColumnDiv>
            <DisplayField label={titleCase(t('reopenDateText').toString())} value={checkDate(claim.clmReopnDte)}/>
          </StyledFourthColumnDiv>)}
      </StyledFlexContainerDiv>

      <Row className='mb-2'>
        <Col sm={6} md={3} size={6} className='mb-2'>
          <DisplayField onTooltipOpenHover={trackTooltipHover('return to work (estimated)')} onTooltipOpenClick={trackTooltipClick('return to work (estimated)')} onTooltipClose={handleTooltipClose} label={titleCase(t('returnToWorkEstimatedText').toString())} value={checkDate(claim.clmExpecRtrnWrkDte)} tooltip={newTranslate(glossaryTerms.rtwEstimated)}/>
        </Col>
        <Col sm={6} md={3} size={6} className='mb-2'>
          <DisplayField onTooltipOpenHover={trackTooltipHover('return to work (modified)')} onTooltipOpenClick={trackTooltipClick('return to work (modified)')} onTooltipClose={handleTooltipClose} label={titleCase(t('returnToWorkModifiedText').toString())} value={checkDate(claim.partDtyRtrnWrkDte)} tooltip={newTranslate(glossaryTerms.rtwModified)}/>
        </Col>
        <Col sm={6} md={6} size={6} className='mb-2'>
          <DisplayField onTooltipOpenHover={trackTooltipHover('return to work (actual)')} onTooltipOpenClick={trackTooltipClick('return to work (actual)')} onTooltipClose={handleTooltipClose} label={titleCase(t('returnToWorkActualText').toString())} value={checkDate(claim.fullDtyRtrnWrkDte)} tooltip={newTranslate(glossaryTerms.rtwActual)}/>
        </Col>
      </Row>

      {/*This is getting hard to follow. If it's approved/closed it will show this text pending smallmid false and STDLTD*/}
      {isNotPendingInprogressStatus && isNotDeniedStatus
            ? user?.customerInContext?.smallMid
                ? ''
                : isSTDLTD && (<Row className={'ml-0'} data-testid='date-disclaimer'>
                <StatusMessage>
                  <Icon size='sm' name='important-message' color='grayDark' className='ml-2 mr-2'/>
                  <i>* {t('dateDisclaimerText')}</i>
                </StatusMessage>
              </Row>)
            : ''}
    </Card>);
};
export default ClaimDatesCard;
